import {Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {KeyValue} from "@angular/common";
import {Order} from "../../../model/order";
import {OrderService} from "../../../service/order.service";
import {PropertyService} from "../../../service/property.service";
import {Router} from "@angular/router";
import {DownloadService} from "../../../service/download.service";
import {Document} from "../../../model/document";
import {Observable, Subject, Subscription} from "rxjs";
import {StorageService} from "../../../service/storage.service";
import {Property} from "../../../model/property";
import {filter, map, startWith, takeUntil} from "rxjs/operators";
import {debounce} from "lodash";
import {LoginIdService} from "../../../service/login-id.service";
import {UserGroup} from "../../../model/userGroup";
import { UserCompanyGroup } from "../../../model/userCompanyGroup";
import { PermissionService } from "../../../service/permission.service";
import { HttpHeaders } from "@angular/common/http";
import {FormControl} from "@angular/forms";
import {User} from "../../../model/user";
import {NgxIndexedDBService} from "ngx-indexed-db";
import {MatPaginator, PageEvent} from "@angular/material/paginator";
import {UserServiceService} from "../../../service/user-service.service";

@Component({
  selector: 'app-order',
  templateUrl: './order.component.html',
  styleUrls: ['./order.component.css']
})
export class OrderComponent implements OnInit, OnDestroy {

  archivedOrders: Order[] = [];
  orders: Order[] = [];
  filteredOrders: Order[] = [];
  currentUser: User;
  documents: Document [] = [];
  document: Document;
  searchText: string = '';
  propertiesBySearchTerm: Property[] = [];
  foundProperties: boolean = false;
  batchStarted: boolean = false;
  indexDBOrderObserver: Observable<Order>;
  loginIdServiceSubscription: Subscription;
  currentUserGroup: UserGroup;
  privateOrders: boolean = false;
  requestedOrders: boolean = false;
  requestedArchivedOrders: boolean = false;
  loadingOrders: boolean = true;
  selectedUserControl = new FormControl();
  filterAreaVisible: boolean = false;
  showAllOrders: boolean = false;
  availableUsers: User[] = [];
  loadingAvailableUsers: boolean = true;
  archived: boolean = false;
  private stopRequestObservable: Subject<void> = new Subject<void>();
  @ViewChild("matPaginator") paginator: MatPaginator;
  page: number = 0;
  pageSize: number = 15;

  constructor(private orderService: OrderService,
              private userService: UserServiceService,
              private propertyService: PropertyService,
              public loginIdService: LoginIdService,
              private router: Router,
              private downloadService: DownloadService,
              private storageService: StorageService,
              private permissionService: PermissionService,
              private dbService: NgxIndexedDBService) {
    this.searchProperties = debounce(this.searchProperties, 500)
  }

  async ngOnInit(): Promise<any> {
    //clear the orders form indexDb when refreshing the page
    this.dbService.clear("orders").subscribe();
    // wait until the loginIdService has been initialized
    this.loginIdService.loginServiceInitialized$.toPromise().then(() => {
      /** Observer that listens to changes in the sessionStorage for the object with the key "orders". Returns the value of "orders".
       Used to update the view after changing routes, coming back to /order */
      this.indexDBOrderObserver = this.storageService.indexDBChange$.pipe(
              filter(({key}) => key === "orders"),
              map(orders => orders?.value),
              filter(value => value?.companyLoginId === this.loginIdService.getCurrentLoginId())
      );

      /** Subscribe to the sessionOrderObserver to detect changes */
      this.indexDBOrderObserver.subscribe((data) => {
          this.orders.forEach((o, index) => {
              if (o.orderid === data.orderid) {
                  this.orders[index] = data;
              }
          })
      });

      // get the current user (required to filter the orders)
      this.currentUser = JSON.parse(sessionStorage.getItem('user'));

      // always refresh the permissions to allow direct access of new orders after permission changes
      this.permissionService.getUserCompanyGroupsFromPrincipal(true, {headers: new HttpHeaders({'showing-spinner': ''})}).subscribe( res => {
          const groups = [];
          res.forEach(group => {
              groups.push(new UserCompanyGroup(group));
          });
          this.loginIdService.updateUserCompanyGroupsPermissions(groups);
      });

      // listen to loginId changes
      this.loginIdServiceSubscription = this.loginIdService.loginIdChanged$.pipe(startWith(true)).subscribe( async() => {
        this.cancelOrderStatisticRequests();
        if (this.searchText.length > 0) {
          this.propertiesBySearchTerm = [];
          this.searchProperties();
        } else {
          this.archivedOrders = [];
          this.orders = [];
          this.filteredOrders = [];
          this.requestedOrders = false;
          this.requestedArchivedOrders = false;
          this.addOrders(false, true, true);
          // if archived is currently selected, directly fetch archived orders as well
          if (this.archived) {
            this.addOrders(true, false, false);
          }
          this.initAvailableUsers();
        }
      });
    });
  }

  /** Initialize the available Users for the current CompanyLoginId
   * Required to filter orders based on the orderedByUserId
   * Directly applies the filter as well after retrieval
   */
  async initAvailableUsers(): Promise<void> {
    this.loadingAvailableUsers = true;
    this.userService.getUserFromLoginId(this.loginIdService.getCurrentLoginId()).subscribe(res => {
      this.loadingAvailableUsers = false;
      this.availableUsers = res;
      this.selectedUserControl.setValue(this.currentUser.id);
      this.selectedUserControl.valueChanges.subscribe(() => {
        this.applyFilters();
      });
    });
  }

  private getOptions(showSpinner: boolean) {
      let httpOptions = {
          params: undefined,
          headers: showSpinner ? this.loginIdService.getCurrentHeaders() : this.loginIdService.getCurrentCustomHeadersWithoutSpinner()
      }
     httpOptions.params = this.getRequestParams(this.page, this.pageSize)
    return httpOptions;
  }

  getRequestParams(page: number, pageSize: number): any {
    let params: any = {};
    if (page) {
      params[`page`] = page;
    }
    if (pageSize) {
      params[`size`] = pageSize;
    }
    return params;
  }

  searchProperties() {
    if (this.searchText.length > 0) {
      let observable = this.propertyService.findPropertiesBySearchTerm(this.searchText.trim(), this.getOptions(true));
      observable.toPromise().then(data => {
        this.propertiesBySearchTerm = data;
      });
    } else {
      this.propertiesBySearchTerm = [];
    }
  }

  downloadForOrder(order: Order, onlyNew = false) {
    this.batchStarted = this.downloadService.downloadForOrder(order, onlyNew);
  }

  amountOfNewDocs(order: Order) {
      if(order.orderStatistics.numberOfNotDownloadedDocumentFiles) {
          return order.orderStatistics.numberOfNotDownloadedDocumentFiles;
      } else {
          return 0;
      }
  }

  /**
   * Returns the full name of the user who placed the given order.
   * If the user name is not available 'Besteller unbekannt' is returned as fallback
   *
   * @param order Order from which to retrieve the name of the user who placed the oder
   * @returns Full name of the ordering user or fallback string if unavailable
   */
  public getOrderedByUsername(order: Order): string {
    return order.orderStatistics?.orderedByUserName ?? 'Besteller unbekannt';
  }

  amountOfDocs(order: Order) {
      if(order.orderStatistics.numberOfDocumentFiles){
          return order.orderStatistics.numberOfDocumentFiles;
      }
      else{
          return 0;
      }
  }

  progress(order: Order) {
      if(order.orderStatistics.numberOfDocuments && order.orderStatistics.numberOfDocuments > 0){
          return order.orderStatistics.numberOfReceivedDocuments / order.orderStatistics.numberOfDocuments * 100;
      }
      else{
          return 0;
      }
  }

    /**
     * Determines if an order contains a property with an open customer interaction by checking the order statistics numberOfOpenCustomerInteractions > 0
     *
     * @param order Order which should be checked for open customer interactions
     * @return True if the orderStatistics of an order are present and numberOfOpenCustomerInteractions of the orderStatistics is > 0 else false
     */
    public hasOrderPropertyWithNecessaryActions(order: Order): boolean {
        return order.orderStatistics && order.orderStatistics.numberOfOpenCustomerInteractions > 0
    }

    /**
     * Determines if an order contains a document with unseen information by checking the order statistics numberOfUnseenDocumentStatus > 0
     * @param order Order which should be checked for documents with unseen information
     * @return True if the orderStatistics of an order are present and numberOfUnseenDocumentStatus of the orderStatistics is > 0 else false
     */
    public containsOrderDocumentWithUnseenInformation(order: Order): boolean {
        return order.orderStatistics && order.orderStatistics.numberOfUnseenDocumentStatus > 0
    }

  hasPropertyNecessaryActions(property: Property) {
    for (const customerInteraction of property.customerInteractions) {
      if (customerInteraction.status.toString() === "OPEN") {
        return true;
      }
    }
    return false;
  }

  containsPropertyDocumentWithUnseenInformation(property) {
    for (const document of property.documents) {
      if (document.statusInformation.find(s => s.seen === false)) {
        return true;
      }
    }
    return false;
  }

  setFoundProperties() {
    let numberOfFoundProperties = document.querySelectorAll('#outer-container #property').length;
    this.foundProperties = numberOfFoundProperties > 0;
  }

  hasOrderStatistics(order: Order) {
    return !!(order.orderStatistics);
  }

  hasPermissionsToDownload(order: Order) : boolean {
    return (
        this.privateOrders ||
        this.currentUserGroup.roles.includes("ADMIN") ||
        order.permission.scopes.includes("DOWNLOAD")
    )
  }

  insufficientPermissionsText(order: Order) : string {
    let tooltip;
    if(!this.loginIdService.hasPermissionsForOrder(order, 'DOWNLOAD')) {
      tooltip =  'Sie haben noch keine Berechtigung diese Dokumente herunterzuladen!'
    } else {
      tooltip =  ''
    }
    return tooltip;
  }

  sortOrders(orders: Array<Order>): Array<Order> {
    return orders.sort((a, b) => new Date(b.entrydate).getTime() - new Date(a.entrydate).getTime());
  }

  orderIdDescending = (
    a: KeyValue<number, Order>,
    b: KeyValue<number, Order>
  ): number => {
    return b.value.orderid.localeCompare(a.value.orderid);
  };

  /**
   * Fetches Orders and stores them either in this.orders or this.archivedOrders depending on the archived boolean
   * Adds a companyLoginId and/or a permissions object to each retrieved order
   * @param archived A boolean to determine which orders to fetch and where to store them
   * @param filter A boolean to determine if the filter function should be called
   * @param initialOrderLengthCheck A boolean to determine if the current users orders should be checked or not
   */
  addOrders(archived: boolean, filter: boolean, initialOrderLengthCheck: boolean) {
    this.loadingOrders = true;
    this.orderService.getMultiUserOrders(archived, this.getOptions(false)).subscribe(async (orders) => {
      if (archived) {
        this.archivedOrders = orders;
        this.requestedArchivedOrders = true;
      } else {
        this.requestedOrders = true;
        this.orders = orders;
      }
      for (let order of orders) {
        order.companyLoginId = this.loginIdService.getCurrentLoginId();
        if (!this.loginIdService.isAdmin()) {
          order.permission = this.loginIdService.getOrderPermissionEntry(order.orderid);
        }
      }

      if (filter) {
        this.loadingOrders = false;
        await this.applyFilters(false, initialOrderLengthCheck);
      }
    });
  }

    /**
     * Gets and set the order statistics for each order of the currently selected pagination data retrieved from getPagedData
     * The order statistics are gotten in batches of a fixed size (currently 5)
     */
    private async setOrderStatistics(): Promise<void> {
      const batchSize = 5;
      const totalOrders = this.getPagedData().length;

      for (let i = 0; i < totalOrders; i += batchSize) {
        const batchOrders = this.getPagedData().slice(i, i + batchSize);

        const promises = batchOrders.map(async (order) => {
          const indexedOrder = await this.dbService.getByKey('orders', order.orderid).toPromise() as Order;
          if(indexedOrder && indexedOrder.orderStatistics) {
            order.orderStatistics = indexedOrder.orderStatistics;
          } else {
            order.orderStatistics = await this.orderService.getOrderStatistics(order.orderid, this.getOptions(false))
                .pipe(takeUntil(this.stopRequestObservable)).toPromise();
            await this.storageService.setIndexedDbItem({ key: "orders", value: order });
          }
        });

        await Promise.all(promises);
      }
    }

    /**
     * Cancel all current requests and prevent future requests to start
     */
    public cancelOrderStatisticRequests(): void{
        this.stopRequestObservable.next();
    }

  /**
   * Pagination change event triggering the setOrderStatistics
  */
  onPageChange(event: PageEvent) {
    this.page = event.pageIndex;
    this.pageSize = event.pageSize;
    this.cancelOrderStatisticRequests();
    this.setOrderStatistics();
  }

  /**
   * Paginates the displayed orders based on the selected pagination
   */
  getPagedData(): Order[] {
    const startIndex = this.page * this.pageSize;
    const endIndex = startIndex + this.pageSize;
    return this.filteredOrders.slice(startIndex, endIndex);
  }

  /**
   * Applies the filters based on the current status of booleans (filterArea, showAllOrders and archived)
   * @param firstPage A boolean to determine if the paginator should be reset to the first page
   * @param initialOrderCheck A boolean to determine if showAllOrders should be displayed instead of showOwnOrders
   */
  async applyFilters(firstPage: boolean = true, initialOrderCheck: boolean = false): Promise<void> {
    if (this.archived) { // if archived is true disable the filterarea and show all retrieved orders
      this.filterAreaVisible = false;
      this.filteredOrders = this.archivedOrders;
    } else { //if archived is false either apply filters or show own/all orders
      if (this.showAllOrders) {
        this.filteredOrders = this.filterAreaVisible ? this.orders.filter(order => order.orderedByUserId === this.selectedUserControl.value) : this.orders;
      } else {
        if (initialOrderCheck) { // check if own orders are empty and show all orders instead then
          const userOrders = this.orders.filter(order => order.orderedByUserId === this.currentUser.id);
          this.checkPersonalOrderLengthAndSwitchIfEmpty(userOrders);
        } else { // show own orders per default
          this.filteredOrders = this.orders.filter(order => order.orderedByUserId === this.currentUser.id);
        }
      }
    }

    if (firstPage && this.filteredOrders.length > 0) {
      this.paginator.firstPage();
    }
    await this.setOrderStatistics();
  }

  /**
   * Checks if the personal orders are empty and show all orders instead then
   * @param userOrders The already filtered list of orders based on the current users userId
   * @private
   */
  private checkPersonalOrderLengthAndSwitchIfEmpty(userOrders: Order[]) {
    if (userOrders.length === 0) { // if the user does not have any own orders just switch to showAllOrders
      this.showAllOrders = true;
      this.filteredOrders = this.orders;
    } else {
      this.filteredOrders = userOrders;
    }
  }

  /**
   * Toggles the filter area and applies the filters
   */
  toggleFilterArea(): void {
    if (this.archived) {
      this.archived = false;
      if (!this.showAllOrders) {
        this.showAllOrders = true;
      }
    } else {
      this.showAllOrders = true;
    }
    this.filterAreaVisible = !this.filterAreaVisible;
    this.applyFilters();
  }

  /**
   * Switches between the display of all and own orders
   * Applies filters as well after switching
   * @param showAll A boolean to switch between all and own orders
   */
  changeShownOrders(showAll: boolean) {
      if (this.archived) {
        this.archived = false;
      }

      this.showAllOrders = showAll;

      if(!this.showAllOrders) {
        this.filterAreaVisible = false;
      }

      this.applyFilters();
    }

  /**
   * Show archived orders, disable filters and allOrders highlighting
   * Fetch archived orders if they were not fetched before
   */
  showArchivedOrders(): void {
    if(!this.archived) {
      this.archived = true;
      this.showAllOrders = false;
      this.filterAreaVisible = false;
      if (!this.requestedArchivedOrders) {
        this.addOrders(true, true, false);
      } else {
        this.applyFilters();
      }
    }
  }

  /**
   * Deal with the emitted event and remove the order from the respective list and push it into the other one
   * @param $event The updated order after archiving/restoring
   */
  processArchiveResponse($event: Order) {
    if ($event.archived) {
      // reassign the arrays for change detection and sort them again to maintain proper ranking
      this.orders = this.sortOrders([...this.orders.filter(order => order.orderid != $event.orderid)]);
      this.archivedOrders = this.sortOrders([...this.archivedOrders, $event]);
    } else {
      this.archivedOrders = this.sortOrders([...this.archivedOrders.filter(order => order.orderid != $event.orderid)]);
      this.orders = this.sortOrders([...this.orders, $event]);
    }

    this.applyFilters(false);

    // automatically navigate to the previous page if no objects remain on the current page
    if(this.getPagedData().length === 0 && this.page !== 0) {
      this.paginator.previousPage();
    }
  }

  ngOnDestroy(): void {
      this.cancelOrderStatisticRequests();
      if(this.loginIdServiceSubscription){
        this.loginIdServiceSubscription.unsubscribe();
      }
  }
}
